import { useEffect } from 'react'
import { useSetting } from '../../lib/settings'

export const READER_STEPS = [1, 2, 3, 4, 5] as const
export const READER_LEADS = ['tight', 'normal', 'loose'] as const
export const READER_MEASURES = ['narrow', 'normal', 'wide'] as const
export const READER_TINTS = ['page', 'sepia', 'night'] as const
/**
 * `book` is the face the BOOK is set in, which is the default and the only one
 * that prints. The other two are this reader's own preference for their own
 * screen, and reach nothing else.
 */
export const READER_FACES = ['book', 'sans', 'serif'] as const

export type ReaderSettings = {
  /**
   * Which step of the reading scale, by POSITION rather than by size.
   *
   * A stored `4` still means the fourth step after the sizes are redrawn; a
   * stored `1.5rem` would mean a size nobody offers. What a step IS belongs to
   * the stylesheet, which is also where the damping that goes with it lives.
   */
  text: (typeof READER_STEPS)[number]
  lead: (typeof READER_LEADS)[number]
  measure: (typeof READER_MEASURES)[number]
  /**
   * The paper, which is not the theme.
   *
   * `page` is whatever the app is set to, so a reader who has chosen nothing
   * follows their machine into dark as they always did. The other two are a
   * decision about the book that holds wherever the app is.
   */
  tint: (typeof READER_TINTS)[number]
  /**
   * What this reader would rather read it in, where that is not the book's own.
   *
   * The book's face is a property of the book itself and is what a printed copy is
   * set in — the same sefer on paper and on screen. A reader may still prefer
   * something else for their own eyes, so this overrides the face HERE and
   * nowhere else: it does not touch the book, the PDF, or what anybody else
   * opening the same book sees.
   */
  face: (typeof READER_FACES)[number]
}

export const READER_DEFAULTS: ReaderSettings = {
  text: 1,
  lead: 'normal',
  measure: 'normal',
  tint: 'page',
  face: 'book',
}

const oneOf = <T extends readonly string[]>(
  allowed: T,
  raw: unknown,
  fallback: T[number],
): T[number] =>
  typeof raw === 'string' && allowed.includes(raw) ? raw : fallback

/**
 * Read a stored value, and never fail on one.
 *
 * What arrives here was written by some earlier version of this app, so a
 * control that has been dropped or a scale that has been renumbered leaves
 * values behind that no longer resolve. Every one of those reads as the
 * default: a reader whose stored setting has gone stale gets a book, not a
 * blank page. That is the same answer an assistant model gone from the gateway
 * gets, for the same reason.
 */
export function parseReaderSettings(raw: unknown): ReaderSettings {
  if (typeof raw !== 'object' || raw === null) return READER_DEFAULTS
  const o = raw as Record<string, unknown>
  const step = READER_STEPS.find((s) => s === o.text) ?? READER_DEFAULTS.text
  return {
    text: step,
    lead: oneOf(READER_LEADS, o.lead, READER_DEFAULTS.lead),
    measure: oneOf(READER_MEASURES, o.measure, READER_DEFAULTS.measure),
    tint: oneOf(READER_TINTS, o.tint, READER_DEFAULTS.tint),
    face: oneOf(READER_FACES, o.face, READER_DEFAULTS.face),
  }
}

/** The key this owns in the settings store, namespaced by its context. */
const KEY = 'studio.reader'

export function useReaderSettings() {
  const [settings, set] = useSetting<ReaderSettings>(KEY, parseReaderSettings)
  return {
    settings,
    /*
     * One field at a time, since each control changes one and shows it at
     * once — merged into whatever is current rather than into what this render
     * saw, so two controls pressed in quick succession both survive.
     */
    update: (patch: Partial<ReaderSettings>) =>
      set((current) => ({ ...current, ...patch })),
  }
}

/**
 * Put the chosen paper on the page for as long as a book is open.
 *
 * On the ROOT, because the paper is the whole reading page's — the bar, the
 * rail and anything portalled out of the column included. Taken off when the
 * reading surface goes, so it never follows somebody to a list of members: it
 * says what the book is set on, not what the app looks like.
 */
export function useReadingPaper(tint: ReaderSettings['tint']): void {
  useEffect(() => {
    if (tint === READER_DEFAULTS.tint) return
    const root = document.documentElement
    root.setAttribute('data-reading-tint', tint)
    return () => root.removeAttribute('data-reading-tint')
  }, [tint])
}

/**
 * What the reading surface carries so the stylesheet can answer.
 *
 * The default writes no attribute at all: the base rule already states it, and
 * a `data-lead="normal"` beside a rule meaning the same thing is a second place
 * for the default to be written down.
 */
export function readerAttrs(s: ReaderSettings) {
  return {
    'data-text': s.text === READER_DEFAULTS.text ? undefined : String(s.text),
    'data-lead': s.lead === READER_DEFAULTS.lead ? undefined : s.lead,
    'data-measure':
      s.measure === READER_DEFAULTS.measure ? undefined : s.measure,
  }
}
